const isObject = (obj) => obj && typeof obj === 'object';

const deepClone = (o, skipKey = false) => {
	const out = Array.isArray(o) ? [] : {};
	if (!isObject(o)) return out;
	Object.keys(o).forEach((key) => {
		if (skipKey && key === skipKey) return;
		const v = o[key];
		out[key] = isObject(v) ? deepClone(v) : v;
	});
	return out;
};

const deepMerge = (...objects) => (
	objects.reduce((prev, obj) => {
		Object.keys(obj).forEach((key) => {
			const pVal = prev[key];
			const oVal = obj[key];

			if (Array.isArray(pVal) && Array.isArray(oVal)) {
				prev[key] = [...pVal, ...oVal].filter((element, index, array) => array.indexOf(element) === index);
			} else if (isObject(pVal) && isObject(oVal)) {
				prev[key] = deepMerge(pVal, oVal);
			} else {
				prev[key] = oVal;
			}
		});

		return prev;
	}, {})
);

const deepCompare = (obj1, obj2) => {
	let isEqual = true;

	if (obj2 === undefined) return false;

	Object.keys(obj1).forEach((p) => {
		switch (typeof (obj1[p])) {
			case 'object':
				if (!deepCompare(obj1[p], obj2[p])) isEqual = false;
				break;
			default:
				if (obj1[p] !== obj2[p]) isEqual = false;
		}
	});

	Object.keys(obj2).forEach((p) => {
		if (typeof (obj1[p]) === 'undefined') isEqual = false;
	});

	return isEqual;
};

const deepDiff = (obj1, obj2, exclude = []) => {
	const r = {};

	for (var prop in obj1) {
		if (obj1.hasOwnProperty(prop) && prop != '__proto__') {
			if (exclude.indexOf(obj1[prop]) == -1) {

				// check if obj2 has prop
				if (!obj2.hasOwnProperty(prop)) r[prop] = obj1[prop];

				// check if prop is object and
				// NOT a JavaScript engine object (i.e. __proto__), if so, recursive diff
				else if (obj1[prop] === Object(obj1[prop])) {
					const difference = deepDiff(obj1[prop], obj2[prop]);
					if (Object.keys(difference).length > 0) r[prop] = difference;
				}

				// check if obj1 and obj2 are equal
				else if (obj1[prop] !== obj2[prop]) {
					if (obj1[prop] === undefined) r[prop] = 'undefined';
					if (obj1[prop] === null) r[prop] = null;
					else if (typeof obj1[prop] === 'function') r[prop] = 'function';
					else if (typeof obj1[prop] === 'object') r[prop] = 'object';
					else r[prop] = obj1[prop];
				}
			}
		}
	}

	return r;
};

const hasTemplateInObj = (sections = [], needle = '', contents = false) => (
	sections.reduce((acc, section) => {
		const templateName = Object.keys(section)[0]; // Get the template name of the section

		// If template is found return true;
		if (templateName === needle) {
			if (contents) acc.push(section[templateName]);
			else acc.push(1);
		} else if (Array.isArray(section[templateName]?.content)) {
			// If the section has content, recurse into it
			const nestedSections = hasTemplateInObj(section[templateName].content, needle, contents);
			acc = [...acc, ...nestedSections];
		}

		return acc;
	}, [])
);

const extractTemplateFromObj = (sections = [], needle = '', stopAtFirst = true) => (
	deepClone(sections).reduce((acc, section) => {
		const [[templateName, sectionData]] = Object.entries(section);

		if (templateName === needle) {
			if (acc.done) return acc;
			acc.extractedSection.push(section);
			if (stopAtFirst) acc.done = true;
		} else if (Array.isArray(sectionData.content)) {
			const nested = extractTemplateFromObj(
				sectionData.content,
				needle,
				stopAtFirst,
			);

			sectionData.content = nested.filteredSections;

			if (nested.extractedSection.length) {
				acc.extractedSection.push(...nested.extractedSection);
				if (stopAtFirst) acc.done = true;
			}

			acc.filteredSections.push(section);
		} else {
			acc.filteredSections.push(section);
		}

		return acc;
	}, { filteredSections: [], extractedSection: [], done: false })
);

const hasAttrInObj = (sections = [], needle = '', contents = false) => (
	sections.reduce((acc, section) => {
		const templateName = Object.keys(section)[0]; // Get the template name of the section

		// If template is found return true;
		if (section[templateName]?.attrs?.[needle]) {
			if (contents) acc.push(section[templateName].attrs[needle]);
			else acc.push(1);
		} else if (Array.isArray(section[templateName]?.content)) {
			// If the section has content, recurse into it
			const nestedSections = hasAttrInObj(section[templateName].content, needle, contents);
			acc = [...acc, ...nestedSections];
		}

		return acc;
	}, [])
);

export { deepClone, deepMerge, deepCompare, deepDiff, hasTemplateInObj, hasAttrInObj, extractTemplateFromObj };
